/**
 * @architecture-header 1
 * @layer module
 * @domain support
 * @responsibility Create support cases and append reply messages; keep the reporter's own list,
 *   the Organization admin list, and the open case detail cache all consistent.
 * @state-owner TanStack Query cache
 * @allowed-imports app environment; same-module query; active-role provider; auth session;
 *   TanStack Query
 * @forbidden routes; app bootstrap; direct Supabase client; localStorage
 * @public-api via modules/support/index.ts
 * @side-effects authenticated POST request outside demo mode
 * @naming-hook POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-path plural lowercase nouns; no version, role or verb segment; a message is created
 *   under its case, not addressed by its own top-level collection
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/sh-f07.spec.ts
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import {
  type OrganizationSupportCaseSummary,
  type SupportCase,
  type SupportCaseSummary,
  supportCaseKeys,
  supportCaseSchema,
} from './support-cases.queries';

function toSummary(supportCase: SupportCase): SupportCaseSummary {
  const latest = supportCase.messages.at(-1);
  return {
    id: supportCase.id,
    subject: supportCase.subject,
    status: supportCase.status,
    revision: supportCase.revision,
    createdAt: supportCase.createdAt,
    updatedAt: latest?.createdAt ?? supportCase.createdAt,
    latestMessagePreview: latest?.body ?? '',
  };
}

function toOrganizationSummary(
  supportCase: SupportCase,
  reporterUserId: string,
  reporterDisplayName: string,
): OrganizationSupportCaseSummary {
  return { ...toSummary(supportCase), reporterUserId, reporterDisplayName };
}

export function useCreateSupportCase() {
  const environment = useWorkspaceEnvironment();
  const organizationId = useActiveOrganizationId();
  const { session } = useWorkspaceSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      subject,
      message,
      idempotencyKey,
    }: {
      subject: string;
      message: string;
      idempotencyKey: string;
    }): Promise<SupportCase> => {
      if (environment.demoMode) {
        const now = new Date().toISOString();
        return {
          id: `demo-case-${Date.now()}`,
          subject,
          status: 'open',
          revision: 1,
          createdAt: now,
          reporterUserId: session?.userId ?? 'demo-user',
          messages: [
            {
              authorId: session?.userId ?? 'demo-user',
              authorRole: 'reporter',
              body: message,
              createdAt: now,
            },
          ],
        };
      }
      // organizationId is deliberately never sent -- the server derives it from the actor's own
      // active membership (round-1 finding 4); a client-supplied value is rejected (extra="forbid").
      return supportCaseSchema.parse(
        await environment.apiClient.request<unknown>('/api/v1/support/cases', {
          method: 'POST',
          body: { subject, message, idempotencyKey },
        }),
      );
    },
    onSuccess: (supportCase) => {
      queryClient.setQueryData(supportCaseKeys.detail(supportCase.id), supportCase);
      queryClient.setQueryData<SupportCaseSummary[]>(supportCaseKeys.mine(), (current) => [
        toSummary(supportCase),
        ...(current ?? []),
      ]);
      if (organizationId) {
        const reporterDisplayName = session?.displayName || session?.email || '';
        queryClient.setQueryData<OrganizationSupportCaseSummary[]>(
          supportCaseKeys.organization(organizationId),
          (current) => [
            toOrganizationSummary(supportCase, session?.userId ?? '', reporterDisplayName),
            ...(current ?? []),
          ],
        );
      }
    },
  });
}

export function useCreateSupportCaseMessage(caseId: string) {
  const environment = useWorkspaceEnvironment();
  const organizationId = useActiveOrganizationId();
  const { session } = useWorkspaceSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      idempotencyKey,
    }: {
      body: string;
      idempotencyKey: string;
    }): Promise<SupportCase> => {
      if (environment.demoMode) {
        const previous = queryClient.getQueryData<SupportCase>(supportCaseKeys.detail(caseId));
        if (!previous) throw new Error('SUPPORT_CASE_NOT_FOUND');
        const now = new Date().toISOString();
        return {
          ...previous,
          status: previous.status === 'needs_info' ? 'in_progress' : previous.status,
          revision: previous.revision + 1,
          messages: [
            ...previous.messages,
            {
              authorId: session?.userId ?? 'demo-user',
              authorRole: 'reporter',
              body,
              createdAt: now,
            },
          ],
        };
      }
      return supportCaseSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/v1/support/cases/${encodeURIComponent(caseId)}/messages`,
          { method: 'POST', body: { body, idempotencyKey } },
        ),
      );
    },
    onSuccess: (supportCase) => {
      queryClient.setQueryData(supportCaseKeys.detail(supportCase.id), supportCase);
      queryClient.setQueryData<SupportCaseSummary[]>(supportCaseKeys.mine(), (current) =>
        current?.map((item) => (item.id === supportCase.id ? toSummary(supportCase) : item)),
      );
      if (organizationId) {
        queryClient.setQueryData<OrganizationSupportCaseSummary[]>(
          supportCaseKeys.organization(organizationId),
          (current) =>
            current?.map((item) =>
              item.id === supportCase.id
                ? {
                    ...toSummary(supportCase),
                    reporterUserId: item.reporterUserId,
                    reporterDisplayName: item.reporterDisplayName,
                  }
                : item,
            ),
        );
      }
    },
  });
}
